import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useRef } from "react";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { api, getFunnelData, clearFunnelData, captureFunnelFromUrl } from "@/lib/api";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Feed from "@/pages/Feed";
import ItemDetail from "@/pages/ItemDetail";
import CreateListing from "@/pages/CreateListing";
import Profile from "@/pages/Profile";
import PublicProfile from "@/pages/PublicProfile";
import Requests from "@/pages/Requests";
import Chat from "@/pages/Chat";
import Premium from "@/pages/Premium";
import Admin from "@/pages/Admin";
import Gruppo from "@/pages/Gruppo";
import FacebookImport from "@/pages/FacebookImport";
import PaymentSuccess from "@/pages/PaymentSuccess";
import MapView from "@/pages/MapView";
import PublicShell from "@/components/PublicShell";
import Layout from "@/components/Layout";

function LoadingScreen() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="h-10 w-10 rounded-full border-2 border-primary/30 border-t-primary animate-spin" />
    </div>
  );
}

function ProtectedRoute({ children, adminOnly }) {
  const { user, loading } = useAuth();
  if (loading || user === null) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" replace />;
  if (adminOnly && user.role !== "admin") return <Navigate to="/app" replace />;
  return children;
}

function AuthCallback() {
  const navigate = useNavigate();
  const processed = useRef(false);
  useEffect(() => {
    if (processed.current) return;
    processed.current = true;
    const hash = window.location.hash;
    const sid = new URLSearchParams(hash.replace("#", "")).get("session_id");
    (async () => {
      try {
        const { data } = await api.post("/auth/google", { session_id: sid, ...getFunnelData() });
        clearFunnelData();
        if (data.token) localStorage.setItem("token", data.token);
        window.location.replace("/app");
      } catch {
        window.location.replace("/login");
      }
    })();
  }, [navigate]);
  return <LoadingScreen />;
}

function AppRouter() {
  const location = useLocation();
  useEffect(() => { captureFunnelFromUrl(); }, [location.search]);
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/gruppo" element={<Gruppo />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/payment/success" element={<PaymentSuccess />} />
      <Route path="/mappa" element={<MapView />} />
      <Route path="/esplora" element={<PublicShell><Feed /></PublicShell>} />
      <Route path="/item/:id" element={<PublicShell><ItemDetail /></PublicShell>} />
      <Route element={<ProtectedRoute><Layout /></ProtectedRoute>}>
        <Route path="/app" element={<Feed />} />
        <Route path="/new" element={<CreateListing />} />
        <Route path="/profile" element={<Profile />} />
        <Route path="/u/:id" element={<PublicProfile />} />
        <Route path="/requests" element={<Requests />} />
        <Route path="/chat/:id" element={<Chat />} />
        <Route path="/premium" element={<Premium />} />
        <Route path="/import" element={<FacebookImport />} />
        <Route path="/admin" element={<ProtectedRoute adminOnly><Admin /></ProtectedRoute>} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <AppRouter />
          <Toaster position="top-right" richColors />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}
